import React, { useEffect, useState } from "react";
import {
  collection,
  doc,
  getDocs,
  getDoc,
  query,
  runTransaction,
  serverTimestamp,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import { Link, useSearchParams } from "react-router-dom";
import apiBaseUrl from "../utils/apiBaseUrl";
import { formatPackageDuration, mergePackages } from "../utils/packages";

const normalizePhone = (value) => {
  const raw = String(value ?? "").replace(/\D/g, "");
  if (!raw) return "";
  if (raw.startsWith("233")) return raw;
  if (raw.startsWith("0")) return `233${raw.slice(1)}`;
  return raw.length === 9 ? `233${raw}` : raw;
};

const normalizeAmount = (value) => {
  const raw = String(value ?? "").replace(/[^0-9.]/g, "");
  const parsed = Number(raw || 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const todayAsInputDate = () => {
  const date = new Date();
  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return localDate.toISOString().slice(0, 10);
};

const buildCredentialSms = ({ name, credentials, reference, packageName }) => {
  const lines = [
    `Hello ${name}, your WiFi access is ready!`,
    `Package: ${packageName}`,
    "",
    "Credentials:",
    ...credentials.map((cred, index) => `Device ${index + 1}: ${cred.username} / ${cred.password}`),
    "",
    `Reference: ${reference}`,
    "Thank you for choosing Chidiz Wi-Fi.",
  ];

  return lines.join("\n");
};

const postApi = async (baseUrl, path, body) => {
  let response;
  try {
    response = await fetch(`${baseUrl}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  } catch {
    throw new Error("Cannot reach the payment server. Open a second terminal and run npm run server.");
  }

  const contentType = response.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) {
    throw new Error(
      `The payment API returned a non-JSON response (HTTP ${response.status}) for ${path}. Redeploy the latest Vercel commit and confirm its /api function routes are available.`
    );
  }

  let data;
  try {
    data = await response.json();
  } catch {
    throw new Error(`The payment API returned invalid JSON (HTTP ${response.status}) for ${path}.`);
  }
  if (!response.ok || !data.success) {
    throw new Error(data.error || "The payment server could not complete the request.");
  }
  return data;
};

export default function TransactionStatus() {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("order") || "";
  const [paidDate, setPaidDate] = useState(todayAsInputDate);
  const [phone, setPhone] = useState("");
  const [amount, setAmount] = useState("");
  const [order, setOrder] = useState(null);
  const [orderLoading, setOrderLoading] = useState(Boolean(orderId));
  const [packages, setPackages] = useState([]);
  const [packageId, setPackageId] = useState("");
  const [loadingPackages, setLoadingPackages] = useState(true);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({
    type: "idle",
    title: "Not checked yet",
    message: "Your payment has not been verified. Check above to see the latest status.",
  });
  const [result, setResult] = useState(null);

  useEffect(() => {
    const loadPackages = async () => {
      try {
        const snapshot = await getDocs(collection(db, "packages"));
        const savedPackages = snapshot.docs.map((packageDoc) => ({
          id: packageDoc.id,
          ...packageDoc.data(),
        }));
        const loadedPackages = mergePackages(savedPackages);
        setPackages(loadedPackages);
        if (loadedPackages.length && !orderId) setPackageId(loadedPackages[0].id);
      } catch (error) {
        console.error("Package loading error:", error);
        setStatus({
          type: "error",
          title: "Packages unavailable",
          message: "We could not load Wi-Fi packages. Please try again later or contact support.",
        });
      } finally {
        setLoadingPackages(false);
      }
    };

    loadPackages();
  }, [orderId]);

  useEffect(() => {
    if (!orderId) {
      setOrderLoading(false);
      return undefined;
    }

    let active = true;
    const loadOrder = async () => {
      try {
        const orderSnapshot = await getDoc(doc(db, "orders", orderId));
        if (!active) return;
        if (!orderSnapshot.exists()) {
          setStatus({
            type: "error",
            title: "Order not found",
            message: "We couldn’t find this order. You can still check your receipt details below.",
          });
          return;
        }

        const loadedOrder = { id: orderSnapshot.id, ...orderSnapshot.data() };
        setOrder(loadedOrder);
        setPhone(loadedOrder.phone || "");
        setAmount(String(loadedOrder.amount ?? ""));
        setPackageId(loadedOrder.packageId || "");
        if (loadedOrder.status === "fulfilled") {
          if (loadedOrder.credentialId) {
            const credentialSnapshot = await getDoc(doc(db, "credentials", loadedOrder.credentialId));
            if (active && credentialSnapshot.exists()) {
              setResult({
                name: loadedOrder.name || "Customer",
                reference: loadedOrder.paystackReference || "",
                packageName: loadedOrder.packageName || loadedOrder.packageId,
                credentials: [{ id: credentialSnapshot.id, ...credentialSnapshot.data() }],
              });
            }
          }
          setStatus({
            type: "success",
            title: "Ticket already issued",
            message: "This order has already received Wi-Fi credentials. Check your SMS or contact support if you need help.",
          });
        }
      } catch (error) {
        console.error("Order lookup error:", error);
        if (active) {
          setStatus({
            type: "error",
            title: "Order unavailable",
            message: "We couldn’t load your order. You can still enter the payment details from your receipt below.",
          });
        }
      } finally {
        if (active) setOrderLoading(false);
      }
    };

    loadOrder();
    return () => {
      active = false;
    };
  }, [orderId]);

  const sendSms = async ({ name, recipient, credentials, reference, packageName }) => {
    const msg = buildCredentialSms({ name, credentials, reference, packageName });

    await postApi(apiBaseUrl, "/api/send-sms", { recipient, message: msg, reference });
  };

  const handleCheck = async (event) => {
    event.preventDefault();

    if (!paidDate || !phone || !amount || !packageId) {
      setStatus({
        type: "error",
        title: "Missing details",
        message: "Enter the payment date, mobile money number, amount, and package.",
      });
      return;
    }
    if (orderId && orderLoading) {
      setStatus({
        type: "warning",
        title: "Order still loading",
        message: "Wait for your order details to load, then try again.",
      });
      return;
    }
    if (order?.status === "fulfilled") {
      setStatus({
        type: "success",
        title: "Ticket already issued",
        message: "This order has already received Wi-Fi credentials. Your ticket is shown below.",
      });
      return;
    }
    if (order && order.status !== "pending_payment") {
      setStatus({
        type: "warning",
        title: "Order unavailable",
        message: "This order cannot be completed. Please contact support.",
      });
      return;
    }

    const normalizedPhone = normalizePhone(phone);
    const normalizedAmount = normalizeAmount(amount);
    const selectedPackage = packages.find((item) => item.id === packageId);

    if (
      !normalizedPhone ||
      !/^233\d{9}$/.test(normalizedPhone) ||
      !normalizedAmount ||
      !selectedPackage ||
      !Number.isFinite(Number(selectedPackage.price))
    ) {
      setStatus({
        type: "error",
        title: "Invalid details",
        message: "Check the phone number, amount, and selected package, then try again.",
      });
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const verificationData = await postApi(apiBaseUrl, "/api/paystack/find", {
          date: paidDate,
          phone: normalizedPhone,
          amount: normalizedAmount,
      });

      const verifiedPayment = verificationData.transaction;
      const verifiedPhone = normalizePhone(verifiedPayment.phone);
      const verifiedAmount = normalizeAmount(verifiedPayment.amount);
      const amountInPesewas = Math.round(normalizedAmount * 100);
      const packagePriceInPesewas = Math.round(Number(selectedPackage.price) * 100);

      if (
        verifiedPayment.currency !== "GHS" ||
        verifiedPhone !== normalizedPhone ||
        Math.round(verifiedAmount * 100) !== amountInPesewas
      ) {
        setStatus({
          type: "warning",
          title: "Payment details do not match",
          message: "The details returned by Paystack do not match the phone number, amount, or GHS currency entered. Check the receipt details and try again.",
        });
        return;
      }

      if (
        order &&
        (order.phone !== normalizedPhone ||
          order.packageId !== packageId ||
          Math.round(Number(order.amount) * 100) !== amountInPesewas)
      ) {
        setStatus({
          type: "warning",
          title: "Receipt does not match this order",
          message: "This payment number and amount don’t match the order details. Check the receipt or contact support.",
        });
        return;
      }

      if (amountInPesewas !== packagePriceInPesewas) {
        setStatus({
          type: "warning",
          title: "Package price does not match",
          message: `Payment verified GHS ${verifiedAmount.toFixed(2)}, but the selected package costs GHS ${Number(selectedPackage.price).toFixed(2)}. Choose a package priced at GHS ${verifiedAmount.toFixed(2)}.`,
        });
        return;
      }

      const existingPayment = await getDocs(
        query(collection(db, "MtnTransactions"), where("reference", "==", verifiedPayment.reference))
      );
      if (!existingPayment.empty) {
        setStatus({
          type: "warning",
          title: "Payment already used",
          message: "This payment has already been used to release Wi-Fi credentials.",
        });
        return;
      }

      const credsSnap = await getDocs(
        query(
          collection(db, "credentials"),
          where("packageId", "==", packageId),
          where("used", "==", false)
        )
      );

      const credentials = credsSnap.docs.slice(0, 1).map((credDoc) => ({
        id: credDoc.id,
        ...credDoc.data(),
      }));

      if (!credentials.length) {
        setStatus({
          type: "warning",
          title: "Payment matched",
          message: "Your payment was found, but credentials for this package are currently unavailable. Please contact support and try again later.",
        });
        return;
      }

      const credential = credentials[0];
      const packageName = selectedPackage.name || credential.packageName || packageId;
      const assignedName = verifiedPayment.name || "Customer";
      const transactionReference = verifiedPayment.reference;

      await runTransaction(db, async (transaction) => {
        const paymentRef = doc(db, "MtnTransactions", transactionReference);
        const credentialRef = doc(db, "credentials", credential.id);
        const orderRef = orderId ? doc(db, "orders", orderId) : null;
        const [paymentSnapshot, credentialSnapshot, orderSnapshot] = await Promise.all([
          transaction.get(paymentRef),
          transaction.get(credentialRef),
          orderRef ? transaction.get(orderRef) : Promise.resolve(null),
        ]);

        if (paymentSnapshot.exists()) {
          throw new Error("This Paystack reference has already been used.");
        }
        if (!credentialSnapshot.exists() || credentialSnapshot.data().used !== false) {
          throw new Error("That package credential was just assigned. Please try checking again.");
        }
        if (orderRef && (!orderSnapshot?.exists() || orderSnapshot.data().status !== "pending_payment")) {
          throw new Error("This order has already been completed or is no longer available.");
        }

        const assignedAt = serverTimestamp();
        transaction.update(credentialRef, {
          used: true,
          assignedTo: normalizedPhone,
          assignedAt,
        });
        transaction.set(paymentRef, {
          reference: transactionReference,
          paymentProvider: "paystack",
          packageId,
          name: assignedName,
          phone: normalizedPhone,
          amount: verifiedAmount,
          currency: verifiedPayment.currency,
          status: "verified",
          credentials: [credential],
          credentialId: credential.id,
          assignedAt,
          createdAt: serverTimestamp(),
          ...(orderId ? { orderId } : {}),
        });
        if (orderRef) {
          transaction.update(orderRef, {
            status: "fulfilled",
            paystackReference: transactionReference,
            credentialId: credential.id,
            fulfilledAt: assignedAt,
          });
        }
      });
      if (order) setOrder({ ...order, status: "fulfilled" });

      setResult({
        name: assignedName,
        reference: transactionReference,
        packageName,
        credentials: [credential],
      });

      try {
        await sendSms({
          name: assignedName,
          recipient: normalizedPhone,
          credentials: [credential],
          reference: transactionReference,
          packageName,
        });
        setStatus({
          type: "success",
          title: "Payment verified",
          message: "Your Wi-Fi credentials are ready and an SMS has been sent to your mobile money number.",
        });
      } catch (smsError) {
        console.error("Ticket SMS delivery error:", smsError);
        setStatus({
          type: "warning",
          title: "Payment verified; SMS failed",
          message: "Your Wi-Fi credentials are shown below, but the SMS could not be sent. Please contact support.",
        });
      }
    } catch (error) {
      console.error("Transaction verification error:", error);
      setStatus({
        type: "error",
        title: "Verification failed",
        message: error.message || "An error happened while checking your transaction. Please try again or contact support.",
      });
    } finally {
      setLoading(false);
    }
  };

  const statusTheme = {
    idle: {
      panel: "border-slate-200 bg-white",
      icon: "bg-slate-100 text-slate-600",
      title: "text-slate-900",
      message: "text-slate-600",
    },
    success: {
      panel: "border-emerald-200 bg-emerald-50",
      icon: "bg-emerald-100 text-emerald-700",
      title: "text-emerald-900",
      message: "text-emerald-800",
    },
    warning: {
      panel: "border-amber-200 bg-amber-50",
      icon: "bg-amber-100 text-amber-700",
      title: "text-amber-900",
      message: "text-amber-800",
    },
    error: {
      panel: "border-rose-200 bg-rose-50",
      icon: "bg-rose-100 text-rose-700",
      title: "text-rose-900",
      message: "text-rose-800",
    },
  };
  const currentStatusTheme = statusTheme[status.type] || statusTheme.idle;
  const statusIcon = status.type === "success"
    ? "✓"
    : status.type === "warning"
      ? "!"
      : status.type === "error"
        ? "×"
        : "i";

  return (
    <div className="min-h-screen bg-[#f3f4f6] text-slate-800">
      <div className="mx-auto max-w-[480px] overflow-hidden rounded-b-3xl border-x border-b border-slate-200 bg-white shadow-xl shadow-slate-200/70">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <Link to="/" className="flex items-center gap-3 text-2xl font-bold tracking-tight text-slate-800">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-sky-100 text-sky-600">◔</span>
            Chidiz Wi‑Fi
          </Link>
          <a
            href="tel:0243767677"
            aria-label="Call Chidiz Wi-Fi support"
            className="flex items-center gap-2 rounded-full border border-slate-200 px-3 py-1 text-sm font-medium text-slate-600 transition hover:border-sky-300 hover:bg-sky-50 hover:text-sky-700"
          >
            <span className="inline-block h-2.5 w-2.5 rounded-full bg-sky-500" />
            Help
          </a>
        </div>

        <div className="px-4 pb-4 pt-5">
          <p className="mb-4 text-sm font-semibold text-slate-600">Enter receipt details</p>

          <form onSubmit={handleCheck} className="space-y-2.5">
            <div className="grid grid-cols-2 gap-2.5">
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-700">Date paid</label>
                <input
                  type="date"
                  value={paidDate}
                  onChange={(e) => setPaidDate(e.target.value)}
                  required
                  className="w-full rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-2 text-sm outline-none focus:border-sky-400"
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-700">Amount (GHS)</label>
                <input
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="10.00"
                  inputMode="decimal"
                  required
                  className="w-full rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-2 text-sm outline-none focus:border-sky-400"
                />
              </div>
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-700">Wi-Fi package</label>
              <select
                value={packageId}
                onChange={(e) => setPackageId(e.target.value)}
                required
                disabled={loadingPackages || packages.length === 0}
                className="w-full rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-2 text-sm outline-none focus:border-sky-400 disabled:opacity-60"
              >
                {loadingPackages && <option value="">Loading packages...</option>}
                {!loadingPackages && packages.length === 0 && <option value="">No packages available</option>}
                {packages.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name || formatPackageDuration(item) || "Wi-Fi"} Ticket
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-700">Mobile money number</label>
              <input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="024 123 4567"
                required
                className="w-full rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-2 text-sm outline-none placeholder:text-slate-400 focus:border-sky-400"
              />
            </div>

            <button
              type="submit"
              disabled={loading || loadingPackages || packages.length === 0}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-blue-700 to-blue-500 px-4 py-2.5 text-sm font-bold text-white shadow-lg shadow-blue-200 disabled:cursor-not-allowed disabled:opacity-70"
            >
              <span>⌕</span>
              {loading ? "Checking transaction..." : "Check transaction status"}
            </button>
          </form>

          <div className={`mt-5 rounded-2xl border p-4 shadow-sm ${currentStatusTheme.panel}`}>
            <div className="flex items-start gap-3">
              <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-lg font-bold ${currentStatusTheme.icon}`}>
                {statusIcon}
              </span>
              <div className="min-w-0 pt-0.5">
                <div className={`text-base font-bold leading-snug ${currentStatusTheme.title}`}>{status.title}</div>
                <p className={`pt-1 text-sm leading-5 ${currentStatusTheme.message}`}>{status.message}</p>
              </div>
            </div>
          </div>

          {result && (
            <div className="mt-5 overflow-hidden rounded-2xl border border-blue-200 bg-white shadow-sm">
              <div className="border-b border-blue-100 bg-blue-50 px-4 py-3">
                <div className="text-base font-bold text-blue-950">Your Wi-Fi access ticket</div>
                <div className="mt-0.5 text-xs text-blue-800">Keep these details safe to connect.</div>
              </div>
              <div className="space-y-3 p-4 text-sm">
                <div className="flex justify-between gap-4">
                  <span className="text-slate-500">Customer</span>
                  <span className="text-right font-semibold text-slate-900">{result.name}</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-slate-500">Package</span>
                  <span className="text-right font-semibold text-slate-900">{result.packageName}</span>
                </div>
                <div className="rounded-xl bg-slate-50 p-3">
                  <div className="flex justify-between gap-4">
                    <span className="text-slate-500">Username</span>
                    <span className="break-all text-right font-mono font-semibold text-slate-900">{result.credentials[0].username}</span>
                  </div>
                  <div className="mt-2 flex justify-between gap-4 border-t border-slate-200 pt-2">
                    <span className="text-slate-500">Password</span>
                    <span className="break-all text-right font-mono font-semibold text-slate-900">{result.credentials[0].password}</span>
                  </div>
                </div>
                <div className="truncate text-xs text-slate-500">
                  <span className="font-semibold">Reference:</span> {result.reference}
                </div>
              </div>
            </div>
          )}

          <Link to="/" className="mt-6 block text-center text-sm font-semibold text-blue-700 hover:text-blue-900">
            ← Back to packages
          </Link>
        </div>
      </div>
    </div>
  );
}
